import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

/**
 * SearchableSelect — modern combobox with:
 *   - inline search
 *   - keyboard-friendly click-outside handling
 *   - "Other" option that reveals an inline text input
 *   - selected highlight + smooth open/close
 *
 * Props:
 *   value       string
 *   onChange(v) callback
 *   options     string[]
 *   placeholder string
 *   allowOther  boolean (default true) — appends an "Other…" tail option
 *   testid      base data-testid for the trigger
 *   disabled    boolean
 */
export default function SearchableSelect({
  value = "",
  onChange,
  options = [],
  placeholder = "Select…",
  allowOther = true,
  testid,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [customMode, setCustomMode] = useState(!!value && !options.includes(value) && value !== "");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value && !options.includes(value) && value !== "") setCustomMode(true);
    else if (options.includes(value)) setCustomMode(false);
  }, [value, options]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const pick = (v) => {
    setCustomMode(false);
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  const chooseOther = () => {
    setCustomMode(true);
    onChange("");
    setOpen(false);
    setQuery("");
  };

  const clear = (e) => {
    e.stopPropagation();
    setCustomMode(false);
    onChange("");
    setQuery("");
  };

  const displayLabel = customMode ? "Other" : value || "";

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 rounded-lg border ${open ? "border-primary ring-2 ring-primary/15" : "border-border"} bg-white px-3 h-11 text-sm text-left transition-all ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-primary/60"}`}
        data-testid={testid}
      >
        <span className={`truncate ${displayLabel ? "text-ink" : "text-muted-foreground"}`}>
          {displayLabel || placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {displayLabel && !disabled && (
            <span onClick={clear} className="p-0.5 rounded-full hover:bg-muted transition-colors" role="button" aria-label="Clear">
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-[75] mt-1.5 w-full min-w-[240px] bg-white border border-neutral-200 rounded-xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden animate-fade-up" data-testid={testid ? `${testid}-popover` : undefined}>
          <div className="p-2 border-b border-neutral-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full pl-8 pr-3 h-9 rounded-md bg-neutral-50 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:bg-white"
                data-testid={testid ? `${testid}-search` : undefined}
              />
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto py-1">
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">No matches. Pick <b>Other</b> below.</p>
            )}
            {filtered.map((o) => {
              const selected = value === o && !customMode;
              return (
                <button
                  key={o}
                  type="button"
                  onClick={() => pick(o)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${selected ? "bg-primary/5 text-primary font-medium" : "text-ink hover:bg-muted/70"}`}
                  data-testid={testid ? `${testid}-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}` : undefined}
                >
                  <span className="truncate">{o}</span>
                  {selected && <Check className="h-4 w-4 shrink-0" />}
                </button>
              );
            })}
            {allowOther && (
              <button
                type="button"
                onClick={chooseOther}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left border-t border-border transition-colors ${customMode ? "bg-primary/5 text-primary font-medium" : "text-ink hover:bg-muted/70"}`}
                data-testid={testid ? `${testid}-opt-other` : undefined}
              >
                <span>Other…</span>
                {customMode && <Check className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
      )}

      {customMode && (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your custom value"
          className="mt-2 w-full rounded-lg border border-border bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/60"
          data-testid={testid ? `${testid}-other-input` : undefined}
        />
      )}
    </div>
  );
}
